import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { RootState } from '../../../../@types/RootState';
import { SegmentsCurve } from '../../../../pages/IPA/Estrategia/pages/RegrasDePreco/lib';
import { Segment } from '../../../gerenciadorPrecos/segmentos';

export type RegisterLimitStoreCattegoryKeys = Segment['field'] | SegmentsCurve | 'storeIds';

type ValueData = Partial<Record<string, string[]>>;

export type RegisterLimitStoreCattegory = {
  rows: number;
  selectedCattegories: RegisterLimitStoreCattegoryKeys[];
  segments: Segment[];
  value: ValueData;
  cache: Partial<Record<string, DataItem<'description', string>[]>>;
  data: Partial<Record<string, DataItem[]>>;
  search: Partial<Record<string, string>>;
};

export type CacheDataItem = RegisterLimitStoreCattegory['cache'][number][number];

export const INITIAL_ROWS = 1;

const initialState: RegisterLimitStoreCattegory = { rows: INITIAL_ROWS, selectedCattegories: [], segments: [], value: {}, cache: {}, data: {}, search: {} };

const slice = createSlice({
  name: 'ipa/limits/register/cattegory',
  initialState,
  reducers: {
    resetCattegory: () => initialState,
    setRows: (state, action: PayloadAction<number>) => {
      state.rows = action.payload;
    },
    incrementRows: state => {
      state.rows++;
    },
    decrementRows: state => {
      state.rows--;
    },
    setSelectedCattegories: (state, action: PayloadAction<RegisterLimitStoreCattegoryKeys[]>) => {
      state.selectedCattegories = action.payload;
    },
    addSelectedCattegories: (state, action: PayloadAction<RegisterLimitStoreCattegoryKeys>) => {
      state.selectedCattegories.push(action.payload);
    },
    setSelectedCattegoriesByIndex: (state, action: PayloadAction<{ index: number; cattegory: RegisterLimitStoreCattegoryKeys }>) => {
      state.selectedCattegories[action.payload.index] = action.payload.cattegory;
    },
    removeSelectedCattegories: (state, action: PayloadAction<RegisterLimitStoreCattegoryKeys>) => {
      state.selectedCattegories = state.selectedCattegories.filter(cattegory => cattegory !== action.payload);
    },
    setSegments: (state, action: PayloadAction<Segment[]>) => {
      state.segments = action.payload;
    },
    setValue: (state, action: PayloadAction<ValueData>) => {
      state.value = action.payload;
    },
    setValueByKey: (state, action: PayloadAction<{ key: string; value: string[] }>) => {
      state.value[action.payload.key] = action.payload.value;
    },
    setCache: (state, action: PayloadAction<Partial<Record<string, CacheDataItem[]>>>) => {
      state.cache = action.payload;
    },
    addCacheByKey: (state, action: PayloadAction<{ key: string; item: CacheDataItem }>) => {
      const { key, item } = action.payload;

      if (!state.cache[key]) state.cache[key] = [];

      const dataItemAlreadyExistsOnCache = state.cache[key]?.some(i => i.value === item.value);

      if (!dataItemAlreadyExistsOnCache) state.cache[key]?.push(item);
    },
    removeCacheByKey: (state, action: PayloadAction<{ key: string; value: DataItem['value'] }>) => {
      const filteredData = state.cache[action.payload.key].filter(({ value }) => value !== action.payload.value);
      state.cache[action.payload.key] = filteredData;
    },
    setCacheByKey: (state, action: PayloadAction<{ key: string; cache: CacheDataItem[] }>) => {
      state.cache[action.payload.key] = action.payload.cache;
    },
    resetValueByKey: (state, action: PayloadAction<string>) => {
      state.value[action.payload] = [];
    },
    resetValueByIndex: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      const key = state.selectedCattegories[index];
      delete state.value[key];
    },
    resetCacheByKey: (state, action: PayloadAction<string>) => {
      state.cache[action.payload] = [];
    },
    setData: (state, action: PayloadAction<Partial<Record<string, DataItem[]>>>) => {
      state.data = action.payload;
    },
    setDataByKey: (state, action: PayloadAction<{ key: string; data: DataItem[] }>) => {
      state.data[action.payload.key] = _.uniqBy(action.payload.data, 'value');
    },
    setSearch: (state, action: PayloadAction<Partial<Record<string, string>>>) => {
      state.search = action.payload;
    },
    setSearchByKey: (state, action: PayloadAction<{ key: string; search: string }>) => {
      state.search[action.payload.key] = action.payload.search;
    },
  },
});

export const {
  resetCattegory,
  setRows,
  incrementRows,
  decrementRows,
  setSelectedCattegories,
  addSelectedCattegories,
  setSelectedCattegoriesByIndex,
  removeSelectedCattegories,
  setSegments,
  setValue,
  setValueByKey,
  addCacheByKey,
  removeCacheByKey,
  setCache,
  setCacheByKey,
  resetValueByKey,
  resetValueByIndex,
  resetCacheByKey,
  setData,
  setDataByKey,
  setSearch,
  setSearchByKey,
} = slice.actions;

export const selector = {
  root: (state: RootState) => state.ipa.limits.filters.cattegory,
  rows: (state: RootState) => state.ipa.limits.filters.cattegory.rows,
  selectedCattegories: (state: RootState) => state.ipa.limits.filters.cattegory.selectedCattegories,
  segments: (state: RootState) => state.ipa.limits.filters.cattegory.segments,
  value: (state: RootState) => state.ipa.limits.filters.cattegory.value,
  cache: (state: RootState) => state.ipa.limits.filters.cattegory.cache,
  data: (state: RootState) => state.ipa.limits.filters.cattegory.data,
  search: (state: RootState) => state.ipa.limits.filters.cattegory.search,
} as const;

export default slice.reducer;
